import { createFileRoute } from '@tanstack/react-router';
import { Box, Grid, Paper, Stack, Typography } from '@mui/material';

import { useSEO, useBreadcrumbSchema, PageSEO } from '../lib/seo';
import { Hero } from '../components/ui/Hero';
import { BrandBackdrop } from '../components/brand/BrandBackdrop';
import { Section } from '../components/ui/Section';
import { SectionHeader } from '../components/ui/SectionHeader';
import { CallToActionCard } from '../components/ui/CallToActionCard';
import { LegalDocCard } from '../components/ui/LegalDocCard';

const DTAC_REQUEST_HREF = 'mailto:compliance@neumind.io?subject=DTAC%20request';

interface DataProtectionFact {
  title: string;
  description: string;
}

// Every fact below is sourced from documents already published in this repo:
// residency / encryption / access controls from the DPA Annexes (src/content/legal/dpa.body.html),
// the ICO number from the Sub-processors notice (src/content/legal/subprocessors.body.html),
// DTAC v2 / DCB0129 framing from the Intended Purpose statement (src/content/legal/intended-purpose.body.html).
// Do not add a fact here without a published source to back it.
const DATA_PROTECTION: DataProtectionFact[] = [
  {
    title: 'UK & EEA data residency',
    description:
      'Personal data is processed primarily in the United Kingdom and EEA. Our primary database is hosted in European data centres (AWS RDS, eu-west-2) and authentication runs in a European region.',
  },
  {
    title: 'Encryption in transit and at rest',
    description:
      'All personal data is encrypted in transit using industry-standard TLS (HTTPS). Sensitive data at rest, including health data, is encrypted using AES-256.',
  },
  {
    title: 'Access controls',
    description:
      'Access follows the principle of least privilege with role-based access controls. Administrative access to cloud platforms requires multi-factor authentication and is logged.',
  },
  {
    title: 'No data sold, no third-party use',
    description:
      'We process client data only on documented instructions and never sell it or use it for our own marketing.',
  },
];

interface NamedStatus {
  framework: string;
  status: string;
}

// Named-status strip — only frameworks Neumind already asserts in published repo docs.
// Frameworks the repo does NOT yet assert (DSP Toolkit status, Cyber Essentials) are
// deliberately omitted; see the audit handoff for facts Ellis must supply before adding them.
const NAMED_STATUS: NamedStatus[] = [
  { framework: 'UK GDPR & Data Protection Act 2018', status: 'In scope' },
  { framework: 'UK MDR (general wellness tool, Article 2)', status: 'Non-medical-device' },
  { framework: 'DTAC v2', status: 'Submission — pack on request' },
  { framework: 'DCB0129 clinical safety', status: 'Clinical-safety case on request' },
  { framework: 'ICO registration', status: 'ZB038508' },
];

interface LegalDoc {
  title: string;
  description: string;
  href: string;
  external?: boolean;
  tag?: string;
}

// LEGAL_DOCS mirrors canon.yaml `liveUrls.*` (~/notes/neumind/cso/regulatory/canon.yaml).
// When canon changes, update this array too, or run /compliance-publish sync to surface drift.
const LEGAL_DOCS: LegalDoc[] = [
  {
    title: 'Privacy Notice',
    description: 'How we collect, use, and protect personal data.',
    href: '/privacy',
  },
  {
    title: 'Cookies Notice',
    description: 'What cookies we set on www.neumind.io and why.',
    href: '/cookies',
  },
  {
    title: 'App EULA',
    description: 'End-user licence for the Neumind mobile app.',
    href: '/legal/eula',
  },
  {
    title: 'Platform EULA',
    description: 'End-user licence for the Neumind clinician dashboard.',
    href: '/legal/platform-eula',
  },
  {
    title: 'Data Processing Agreement',
    description: 'Our DPA for organisations processing client data with us.',
    href: '/legal/dpa',
  },
  {
    title: 'Sub-processors',
    description: 'The third parties that process data on our behalf.',
    href: '/legal/subprocessors',
  },
  {
    title: 'Intended Purpose Statement',
    description: 'Our regulatory positioning as a general wellness tool under UK MDR.',
    href: '/legal/intended-purpose',
  },
  {
    title: 'SaaS Terms',
    description: 'The UK B2B SaaS contract template we use for paid clinician and enterprise tiers.',
    href: 'https://share.neumind.io/legal/terms',
    external: true,
    tag: 'B2B contract',
  },
];

export const Route = createFileRoute('/trust')({
  component: TrustPage,
});

export function TrustPage() {
  useSEO(PageSEO.trust);
  useBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Trust', url: '/trust' },
  ]);

  return (
    <>
      <Hero
        variant="gradient"
        centered
        maxWidth="md"
        tagline="Trust & Compliance"
        taglineSx={{ position: 'relative', zIndex: 1 }}
        title="Compliance you can verify"
        renderTitle={(title) => {
          const [lead, accent] = title.split(' you can ');
          return (
            <Typography
              variant="h1"
              sx={{ mb: 3, textAlign: 'center', position: 'relative', zIndex: 1 }}
            >
              {lead}{' you can '}
              <Box component="span" sx={{ color: 'primary.main' }}>
                {accent}
              </Box>
            </Typography>
          );
        }}
      >
        {/* Brand-atmosphere layer: restrained cool mesh wash + a quiet Care Circle
            constellation fading from the top-right shoulder. Kept low-key for the
            compliance/security buyer — credible, not busy. Anchors to the Hero's
            relative outer Box so it spans the full band. Subtle per the
            visual-language brief — blue ≤10%, cyan ≤6%. */}
        <BrandBackdrop
          mesh="light"
          constellation
          constellationCorner="top-right"
          constellationOpacity={0.45}
        />
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            fontSize: '1.25rem',
            textAlign: 'center',
            maxWidth: 720,
            mx: 'auto',
            position: 'relative',
            zIndex: 1,
          }}
        >
          Neumind is a general wellness tool, non-medical-device per UK MDR Article 2.
          We operate under UK GDPR and the Data Protection Act 2018, and our full DTAC
          pack is available on request.
        </Typography>
      </Hero>

      <Section maxWidth="md">
        <SectionHeader
          title="How we protect your data"
          subtitle="The answers a funder or security questionnaire asks for, in plain language."
          centered
        />
        <Grid container spacing={3}>
          {DATA_PROTECTION.map((fact) => (
            <Grid item xs={12} sm={6} key={fact.title}>
              <Paper
                variant="outlined"
                sx={{ p: 3, height: '100%', borderRadius: 3 }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {fact.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {fact.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Section>

      <Section variant="muted" maxWidth="md">
        <SectionHeader
          title="Frameworks and registrations"
          subtitle="Where we stand against the standards procurement teams check for. We name honest status rather than implying certification we do not hold."
          centered
        />
        <Stack spacing={1.5}>
          {NAMED_STATUS.map((item) => (
            <Box
              key={item.framework}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 0.5,
                px: 3,
                py: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {item.framework}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.status}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Section>

      <Section>
        <SectionHeader
          title="Our legal stack"
          subtitle="The full set of published legal documents underpinning Neumind."
          centered
        />
        <Grid container spacing={3}>
          {LEGAL_DOCS.map((doc) => (
            <Grid item xs={12} sm={6} md={4} key={doc.href}>
              <LegalDocCard {...doc} />
            </Grid>
          ))}
        </Grid>
      </Section>

      <Section variant="gradient" maxWidth="md">
        <CallToActionCard
          location="trust_bottom_cta"
          title="Need our full DTAC pack?"
          description="The gated DTAC bundle covers clinical safety, technical security, and data protection in depth. We share it on request for evaluation and procurement review."
          primaryCTA={{
            label: 'Request DTAC pack',
            href: DTAC_REQUEST_HREF,
            external: true,
          }}
          secondaryCTA={{
            label: 'Request via contact form',
            href: 'https://www.neumind.io/contact-us',
            external: true,
          }}
        />
      </Section>
    </>
  );
}
